<script setup>
import { ref, computed, toRefs, watch } from 'vue';

import AppApiInfoButton from '@/components/devtools/ApiInfoButton.vue';
import AppDevTabs from '@/components/devtools/DevTabs.vue';
import AppDevPages from '@/components/devtools/DevPages.vue';

import { useModalStore } from '@/stores/modal';

const modalStore = useModalStore();

const selectedPage = ref('default');

const emits = defineEmits(['hide']);

const props = defineProps({
  header: {
    type: String,
  },
  apiQuery: {
    type: Object,
  },
  apiVariables: {
    type: Object,
    default: null,
  },
  apiSchema: {
    type: Object,
    default: null,
  },
  apiMcpId: {
    type: String,
    default: null,
  },
  closeOnEscape: {
    type: Boolean,
    default: true,
  },
});

const visible = defineModel('visible', { required: true });

const { header, apiQuery, apiVariables, closeOnEscape } = toRefs(props);

const modalId = ref(`modal-${Math.random().toString(36).substring(2, 15)}`);
watch(
  visible,
  (newValue) => {
    if (newValue) {
      modalStore.openedModal(modalId.value);
    } else {
      modalStore.closedModal(modalId.value);
    }
  },
  { immediate: true }
);

const canCloseOnEscape = computed(() => {
  if (!closeOnEscape.value) {
    return false;
  }
  if (modalStore.topModal && modalStore.topModal === modalId.value) {
    return true;
  }
  return false;
});
</script>
<template>
  <Dialog
    :id="modalId"
    v-model:visible="visible"
    :modal="true"
    class="modal"
    :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
    :draggable="false"
    pt:header:class="pb-6 !items-start"
    pt:footer:class="pt-6"
    :ptOptions="{ mergeProps: true }"
    @hide="emits('hide')"
    :closeable="false"
    :closeOnEscape="canCloseOnEscape"
  >
    <template #header>
      <div class="-mr-12 mt-12 w-full">
        <div
          class="flex w-full flex-col flex-wrap items-center justify-center text-center"
        >
          <h2 class="text-3xl font-medium">
            {{ header }}
          </h2>
          <slot name="header" />
          <app-dev-tabs
            v-if="apiQuery"
            :query="apiQuery"
            :variables="apiVariables"
            :jsonSchema="apiSchema"
            :mcp="apiMcpId"
            v-model:value="selectedPage"
          />
        </div>
      </div>
    </template>
    <div v-show="selectedPage === 'default'">
      <slot />
    </div>
    <app-dev-pages
      v-if="apiQuery"
      :selected-page="selectedPage"
      :query="apiQuery"
      :variables="apiVariables"
      :jsonSchema="apiSchema"
      :mcp="apiMcpId"
    />
    <template #footer v-if="$slots.footer && selectedPage === 'default'">
      <slot name="footer" />
    </template>
  </Dialog>
</template>
